import { loadStripe } from '@stripe/stripe-js/pure';
import { computed, ref } from '@vue/composition-api';
import api from '@/api';
import usePayment from '@/compositions/accounting/usePayment';
import PaymentMethod from '@/orm/models/Account/PaymentMethod';

const publicKey = process.env.VUE_APP_STRIPE_PUBLIC_KEY;
let stripe = null;
let elements = null;
let cardNumber = null;
let cardExpiry = null;
let cardCvc = null;

const validInputs = ref([]);

export default function useStripe({
  onSuccess = null,
  onFailure = null,
  inputStyles = {
    fontSize: '16px',
  },
} = {}) {
  const error = ref(null);
  const success = ref(false);

  const inputCompleted = computed(() => {
    return validInputs.value.length === 3;
  });

  async function load() {
    stripe = await loadStripe(publicKey);
    elements = stripe.elements();
  }

  async function onFailureSafe(payload) {
    if (typeof onFailure === 'function') {
      await onFailure(payload);
    }
  }

  async function onSuccessSafe(payload) {
    if (typeof onSuccess === 'function') {
      await onSuccess(payload);
    }
  }

  function mount() {
    cardNumber = elements.create('cardNumber', {
      style: {
        base: inputStyles,
      },
    });

    cardExpiry = elements.create('cardExpiry', {
      style: {
        base: inputStyles,
      },
    });

    cardCvc = elements.create('cardCvc', {
      style: {
        base: inputStyles,
      },
    });

    cardNumber.mount('#card-number');
    cardExpiry.mount('#card-date');
    cardCvc.mount('#card-cvc');

    [cardNumber, cardExpiry, cardCvc].forEach((e) => e.on('change', (event) => {
      if (event.error) {
        error.value = event.error.message;
      } else {
        if (event.complete && !validInputs.value.some((field) => event.elementType === field)) {
          validInputs.value.push(event.elementType);
        } else {
          validInputs.value = validInputs.value.filter((code) => code !== event.elementType);
        }
        error.value = '';
      }
    }));
  }

  function storePaymentMethod(id) {
    return PaymentMethod.api().store({
      id,
      driver: 'stripe',
    });
  }

  async function loadAndMount(selector) {
    await load();
    mount(selector);
  }

  async function sendIntentWebhook(intent) {
    return api.post('/billing/webhook/stripe', {
      type: `payment_intent.${intent.status}`,
      data: {
        object: {
          id: intent.id,
        },
      },
    });
  }

  function getBillingDetails() {
    const { paymentAddress } = usePayment();
    return {
      address: {
        city: paymentAddress.value?.city,
        country: paymentAddress.value?.country,
        line1: paymentAddress.value?.line1,
        line2: paymentAddress.value?.line2,
        postal_code: paymentAddress.value.postcode,
        state: paymentAddress.value.region,
      },
    };
  }

  async function createSetupIntent() {
    const result = await api.post('/billing/payment/stripe/method/create');
    return result.data;
  }

  async function createSetupIntentWithTrial(planId) {
    const result = await api.post('/billing/payment/stripe/method/createWithTrial', { planId });
    return result.data;
  }

  async function createPaymentMethodFromClientSecret(clientSecret) {
    const result = await stripe.confirmCardSetup(clientSecret,
      {
        payment_method: {
          card: cardNumber,
          billing_details: getBillingDetails(),
        },
      });

    if (result.error) {
      await onFailureSafe(result.error);
      error.value = result.error.message;
    } else {
      const resultStore = await storePaymentMethod(result.setupIntent.payment_method);
      await onSuccessSafe();
      validInputs.value = [];
      return resultStore.data;
    }
  }

  async function createPaymentMethod() {
    const clientSecret = await createSetupIntent();

    return createPaymentMethodFromClientSecret(clientSecret);
  }

  async function createPaymentMethodWithTrial(planId) {
    const clientSecret = await createSetupIntentWithTrial(planId);

    return createPaymentMethodFromClientSecret(clientSecret);
  }

  async function confirmPayment(clientSecret) {
    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardNumber,
        billing_details: getBillingDetails(),
      },
      setup_future_usage: 'off_session',
    });
    if (result.error) {
      await onFailureSafe(result.error);
      error.value = result.error.message;
      await sendIntentWebhook(result.error.payment_intent);
    } else if (result.paymentIntent.status === 'succeeded') {
      // TODO: remove on production
      await sendIntentWebhook(result.paymentIntent);
      if (typeof onSuccess === 'function') {
        await onSuccess();
      }
      success.value = true;
      await storePaymentMethod(result.paymentIntent.payment_method);
    }
  }

  async function startSubscriptionPayment({ priceId, periodId, paymentMethodId }) {
    if (!paymentMethodId && !inputCompleted.value) {
      return;
    }
    const result = await api.post('/billing/payment/stripe/create', {
      priceId, periodId, paymentMethodId,
    });

    if (!result.data && result.status === 200) {
      await onSuccessSafe();
    } else {
      await confirmPayment(result.data);
    }
  }

  async function renewSubscriptionPayment({ priceId, periodId, paymentMethodId }) {
    if (!paymentMethodId && !inputCompleted.value) {
      return;
    }
    const result = await api.post('/billing/payment/stripe/renew', {
      priceId, periodId, paymentMethodId,
    });

    if (!result.data && result.status === 200) {
      await onSuccessSafe();
    } else {
      await confirmPayment(result.data);
    }
  }

  async function prolongSubscriptionPayment() {
    try {
      await api.post('/billing/payment/stripe/prolong_subscription', null);
      await onSuccessSafe();
    } catch (e) {
      await onFailureSafe(e);
    }
  }

  async function upgradePayment({ planId, paymentMethodId }) {
    if (!paymentMethodId && !inputCompleted.value) {
      return;
    }

    try {
      const result = await api.post('/billing/payment/stripe/upgrade', {
        planId,
        paymentMethodId,
      });
      if (!result.data && result.status === 200) {
        await onSuccessSafe();
        success.value = true;
      } else {
        await confirmPayment(result.data);
      }
    } catch (e) {
      await onFailureSafe(e);
    }
  }

  async function quotaPayment({ itemType, count, paymentMethodId }) {
    if (!paymentMethodId && !inputCompleted.value) {
      return;
    }
    const result = await api.post('/billing/payment/stripe/quota', {
      paymentMethodId, itemType, count,
    });

    if (!result.data && result.status === 200) {
      await onSuccessSafe();
    } else {
      await confirmPayment(result.data);
    }
  }

  return {
    load,
    mount,
    loadAndMount,
    startSubscriptionPayment,
    renewSubscriptionPayment,
    upgradePayment,
    error,
    success,
    createPaymentMethod,
    createPaymentMethodWithTrial,
    prolongSubscriptionPayment,
    inputCompleted,
    quotaPayment,
  };
}
